import * as React from 'react';
import type { IFileInputManager } from '@inwink/fileutils/fileapi';
import type { Entities } from '@inwink/entities/entities';
import type {
    ICMSContentTemplatesDatasource,
    ICMSEntityFormTemplatesDatasource,
    IPictureAssetDataSource
} from '../api/base/cmsdatasource';

import type { IEntityPageConfiguration, IConfigurationProviderArgs } from './entities/entitypagev3/entitypagebase.props';
import type { IDashboardDatasource } from './dashboard/definitions';
import type { ICommunityDetail } from '@@community/data/communitydata';
import type { IAssetManagerDatasource, IAssetType } from './assetsmanager/definitions';

// eslint-disable-next-line import/no-cycle
// export { CMSContextWrapper } from './cmscontext.wrapper';

/**
 * ICMSContext is the core interface providing scope-specific functionality and data access
 * for the inwink backoffice sharding architecture.
 * 
 * Each scope (Customer, Tenant, Event, Community) has its own context implementation that extends this interface.
 * The context provides access to entity configurations, page templates, assets, and scope-specific data.
 * 
 * @example
 * // Access via React context (functional components)
 * const cmscontext = React.useContext(CMSContextProvider);
 * 
 * @example
 * // Access via HOC (class components)
 * @withCMSContext()
 * class MyComponent extends React.Component<{cmscontext: ICMSContext}> { }
 * 
 * @see IEventCMSContext for event-specific context
 * @see ICommunityCMSContext for community-specific context
 * @see CMSContextProvider for accessing context in components
 */
export interface ICMSContext {
    /** Customer scope ID (top-level in the hierarchy) */
    customerId?: string;
    
    /** Tenant scope ID (subdivision within a customer) */
    tenantId?: string;
    
    /** 
     * Parent scope context, enabling hierarchical navigation.
     * Example: Event context has Tenant context as parent, which has Customer context as parent.
     */
    parent: ICMSContext;
    
    /** 
     * Current scope identifier (e.g., eventId, communityId, tenantId, customerId)
     * Use this to get the ID of the current scope.
     */
    relatedTo: string;
    
    /** 
     * Scope type: "Event", "Community", "AuthTenant", or "Customer"
     * Use this to identify which scope you're working in.
     */
    relatedKind: string;
    
    /** List of supported languages for the current scope */
    languages: string[];
    
    /** Current display language */
    displayLanguage: string;
    
    /** 
     * File upload and management interface.
     * Used for uploading files, getting transcripts, and managing file assets.
     */
    fileInputManager: IFileInputManager;
    
    /** 
     * Event details (only available when relatedKind === "Event")
     * Use type guard to access: if (cmscontext.eventdetail) { ... }
     */
    eventdetail?: Entities.IEventDetail;
    
    /** 
     * Community details (only available when relatedKind === "Community")
     * Use type guard to access: if (cmscontext.communityDetail) { ... }
     */
    communityDetail?: ICommunityDetail;
    
    /** 
     * Picture assets datasource for querying and managing images 
     */
    pictures: IPictureAssetDataSource;
    
    /** 
     * Get asset datasource for a specific asset type and scope
     * @param assetType - Type of asset (e.g., 'Image', 'Video')
     * @param applicationScope - Scope type (e.g., 'event', 'community')
     * @param applicationScopeId - Scope ID
     */
    assetDatasource?: (assetType?: IAssetType, applicationScope?: string, applicationScopeId?: string) => IAssetManagerDatasource,
    
    /** 
     * Scope-specific configuration object.
     * Type depends on scope: IEventDetailConfiguration for events, ICommunityDetailConfiguration for communities, etc.
     * Consider using ICMSContextBase<TConfig> for type-safe access.
     */
    configuration: any;
    
    /** 
     * Content templates (pages) datasource.
     * Used to query, create, update, publish, and manage content pages for the scope.
     * @see ICMSContentTemplatesDatasource
     */
    contentTemplates: ICMSContentTemplatesDatasource;
    
    /** 
     * Check if a tiny URL is available for use
     * @param application - Application type
     * @param state - State identifier
     * @param type - URL type
     * @param tinyurl - The tiny URL to check
     * @param customProps - Additional custom properties
     * @returns Promise resolving to availability status
     */
    isTinyUrlAvailable: (
        application: string,
        state: string,
        type: string,
        tinyurl: string,
        customProps?: any
    ) => Promise<{ available: boolean }>;
    
    /** 
     * Get entity template with field definitions
     * @param entityname - Name of the entity (e.g., 'person', 'session', 'exhibitor')
     * @param entityParentId - Optional parent entity ID for hierarchical entities
     * @returns Promise resolving to entity template with fields
     * @see getWorkTemplate for extended template with more metadata
     */
    getEntityTemplate: (entityname: string, entityParentId?: string) => Promise<Entities.IEntityTemplate>;
    
    /** 
     * Get work template with extended metadata (includes relationships, views, etc.)
     * @param entityname - Name of the entity
     * @param entityParentId - Optional parent entity ID
     * @returns Promise resolving to extended entity template
     * @see getEntityTemplate for basic template
     */
    getWorkTemplate: (entityname: string, entityParentId?: string) => Promise<Entities.IEntityTemplateV3>;
    
    /** 
     * Get complete entity page configuration (grid + detail views)
     * @param entityname - Name of the entity
     * @param args - Configuration provider arguments (includes cmscontext, rights, etc.)
     * @returns Promise resolving to entity page configuration
     * @example
     * const config = await cmscontext.getEntityConfiguration('person', { cmscontext, rights });
     * // config.gridConfig, config.detailConfig
     */
    getEntityConfiguration<TEntity = Entities.IEntity, TConfigArgs = IConfigurationProviderArgs>(
        entityname: string,
        args: TConfigArgs
    ): Promise<IEntityPageConfiguration<TEntity>>;
    
    /** 
     * Update scope configuration
     * @param config - Updated configuration object
     */
    editConfiguration: (config) => Promise<void>;
    
    /** 
     * Get preview host URL for the current scope
     * @returns Preview host URL
     */
    previewHost(): string;
    
    /** 
     * Get full preview URL for the current scope (if available)
     * @returns Promise resolving to preview URL
     */
    getPreviewUrl?: () => Promise<string>;
    
    /** 
     * Get preview tiny URL for the current scope (if available)
     * @returns Promise resolving to preview tiny URL
     */
    getPreviewTinyUrl?: () => Promise<string>;
    
    /** 
     * Backoffice entity form templates datasource (if available)
     * Used for managing custom entity forms in the backoffice.
     */
    boEntityFormTemplates?: ICMSEntityFormTemplatesDatasource;
    
    /** 
     * Get dashboard datasource for a specific module
     * @param moduleName - Module name (e.g., 'event', 'community')
     * @returns Dashboard datasource instance
     */
    getDashboardDatasource: (moduleName: string) => IDashboardDatasource;
    
    /** 
     * Get route target page for a link (if available)
     * @param link - Link to resolve
     * @param context - Context object
     * @param routeParams - Route parameters
     * @param isFromBackOrga - Whether request is from backoffice
     * @returns Target page identifier
     */
    getRouteTargetPage?: (link: string, context?: any, routeParams?: any, isFromBackOrga?: boolean) => string,
    
    /** 
     * Component configuration getters (if available)
     */
    getComponentConfiguration?: {
        appLinkInput?: (props: any) => any;
    }
    
    /** 
     * Dynamic field key suffix for the current scope.
     * Used for scope-specific dynamic fields (e.g., "cu" for customer, "aud" for tenant).
     */
    dynamicFieldKeySuffix?: string;
    
    /** 
     * Get shell user information by ID (if available)
     * @param userId - User ID
     * @param notifyUpdate - Optional callback for updates
     * @returns User object or null
     */
    getShellUser?: (userId: string, notifyUpdate?: () => void) => ICMSContextUser | null;
    
    /** 
     * Get all shell users (if available)
     * @returns Promise resolving to array of users
     */
    getShellUsers?: () => Promise<ICMSContextUser[]>;
}

export interface ICMSContextUser {
    id: string;
    name: string;
    roles: string[];
}

/** 
 * React context provider for ICMSContext.
 * Use with useContext hook or Consumer pattern to access the current scope's context.
 * 
 * @example
 * // Functional component with hook
 * const cmscontext = React.useContext(CMSContextProvider);
 * 
 * @example
 * // Consumer pattern
 * <CMSContextProvider.Consumer>
 *   {(cmscontext) => <MyComponent cmscontext={cmscontext} />}
 * </CMSContextProvider.Consumer>
 */
export const CMSContextProvider = React.createContext<ICMSContext>(null);

/**
 * Type-safe version of ICMSContext with specific configuration type.
 * Use this when you need type-safe access to the configuration property.
 * 
 * @template TConfig - The configuration type (e.g., IEventDetailConfiguration)
 * 
 * @example
 * const eventContext: ICMSContextBase<Entities.IEventDetailConfiguration> = ...;
 * // eventContext.configuration is now typed as IEventDetailConfiguration
 */
export interface ICMSContextBase<TConfig> extends ICMSContext {
    configuration: TConfig;
}

/**
 * Higher-Order Component (HOC) that injects cmscontext as a prop.
 * Use this decorator on class components to access ICMSContext.
 * 
 * @returns Decorator function that wraps the component with CMSContextProvider.Consumer
 * 
 * @example
 * interface IMyComponentProps {
 *   cmscontext?: ICMSContext;
 * }
 * 
 * @withCMSContext()
 * export class MyComponent extends React.Component<IMyComponentProps> {
 *   render() {
 *     return <div>{this.props.cmscontext.relatedTo}</div>;
 *   }
 * }
 */
export function withCMSContext() {
    return function (target) {
        // eslint-disable-next-line react/display-name
        return React.forwardRef((props, ref) => <CMSContextProvider.Consumer>
            {(cmscontext) => React.createElement(target, { ...props, cmscontext: cmscontext, ref: ref })}
        </CMSContextProvider.Consumer>) as typeof target;
    };
}
